import { handleAsync } from "@/utils";
import { apiWebsocketURL } from "@/utils/env";

import type { TApiResponse } from "../../types/api";
import { ApiService, type TApiServiceError, type TApiServiceOptions } from "../apiService";
import { withAuthHooks } from "../auth/auth.ky.hooks";
import type {
  TCategoryListResponse,
  TCategoryResponse,
  TChatMessage,
  TChatMessagesResponse,
  TChatThread,
  TChatThreadCreatePayload,
  TThreadContextResponse,
  TTopThread,
  TTopThreadsResponse,
} from "./chatThreads.types";

const apiService = new ApiService({
  hooks: {
    ...withAuthHooks,
  },
});

class ChatThreadsService {
  private rootEndpoint = "chat" as const;
  private threadsEndpoint = `${this.rootEndpoint}/threads` as const;
  private websocketPath = "chat/ws/chat" as const;
  private uploadEndpoint = "files/upload" as const;

  getCategories(options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TCategoryListResponse>>(() =>
      apiService.get(`${this.rootEndpoint}/categories`, options)
    );
  }

  getThreads(
    filters?: {
      companyId?: string;
      sectionId?: string;
    },
    options?: TApiServiceOptions
  ) {
    const searchParams = new URLSearchParams();
    if (filters?.companyId) searchParams.set("company_id", filters.companyId);
    if (filters?.sectionId) searchParams.set("section_id", filters.sectionId);

    const queryString = searchParams.toString();
    const endpoint = queryString ? `${this.threadsEndpoint}?${queryString}` : this.threadsEndpoint;

    return handleAsync<
      TApiServiceError,
      TApiResponse<{ threads: TChatThread[]; total: number; page: number; size: number }>
    >(() => apiService.get(endpoint, options));
  }

  createThread(payload: TChatThreadCreatePayload, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TChatThread>>(() =>
      apiService.post(this.threadsEndpoint, payload, options)
    );
  }

  getThread(threadId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TChatThread>>(() =>
      apiService.get(`${this.threadsEndpoint}/${threadId}`, options)
    );
  }

  getThreadMessages(threadId: string, limit = 50, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TChatMessagesResponse>>(() =>
      apiService.get(`${this.threadsEndpoint}/${threadId}/messages?limit=${limit}`, options)
    );
  }

  sendMessage(threadId: string, payload: { content: string; attachments?: unknown[] }, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TChatMessage>>(() =>
      apiService.post(`${this.threadsEndpoint}/${threadId}/messages`, payload, options)
    );
  }

  getMessage(messageId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TChatMessage>>(() =>
      apiService.get(`${this.rootEndpoint}/messages/${messageId}`, options)
    );
  }

  getThreadContext(threadId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TThreadContextResponse>>(() =>
      apiService.get(`${this.threadsEndpoint}/${threadId}/context`, options)
    );
  }

  uploadThreadFile(payload: { threadId: string; file: File; companyId?: string }, options?: TApiServiceOptions) {
    const formData = new FormData();
    formData.append("file", payload.file);

    const params = new URLSearchParams();
    params.set("file_type", "chat_attachment");
    params.set("thread_id", payload.threadId);
    if (payload.companyId) params.set("company_id", payload.companyId);

    const endpoint = `${this.uploadEndpoint}?${params.toString()}`;

    return handleAsync<TApiServiceError, TApiResponse<{ id: string; url?: string }>>(() =>
      apiService.post(endpoint, formData, options)
    );
  }

  getWebSocketURL(threadId: string, token: string) {
    const url = apiWebsocketURL(`${this.websocketPath}/${threadId}`);
    url.searchParams.set("token", token);
    return url.toString();
  }

  getTopThreads(limit = 2, filters?: { companyId?: string }, options?: TApiServiceOptions) {
    const searchParams = new URLSearchParams();
    searchParams.set("limit", String(limit));
    if (filters?.companyId) searchParams.set("company_id", filters.companyId);
    const queryString = searchParams.toString();
    const endpoint = `${this.threadsEndpoint}/top?${queryString}`;
    return handleAsync<TApiServiceError, TApiResponse<TTopThreadsResponse>>(() => apiService.get(endpoint, options));
  }
}

export const chatThreadsService = new ChatThreadsService();
export type {
  TCategoryListResponse,
  TCategoryResponse,
  TChatMessage,
  TChatMessagesResponse,
  TChatThread,
  TChatThreadCreatePayload,
  TThreadContextResponse,
  TTopThread,
  TTopThreadsResponse,
};
